import React, { FC, ReactNode, useState } from "react";

// types
import { ICardNetwork } from "../../types";

// UI
import { Card } from "@UI/Components/Card/Card";
import { Heading } from "@UI/Components/Heading/Heading";
import { Flex } from "@UI/Components/Flex/Flex";
import { HStack } from "@UI/Components/Stack/Stack";
import { Icon } from "@UI/Components/Icon/Icon";
import { Box } from "@UI/Components/Box/Box";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import { CircleXmark } from "@UI/Components/Icons/CircleXmark";
import useMediaQuery from "@UI/hooks/useMediaQuery";

// payment gateway icons
import { MastercardCardIcon } from "../PaymentMethodIcons/Icons/Mastercard/Mastercard";
import { AmericanExpressCardIcon } from "../PaymentMethodIcons/Icons/AmericanExpress/AmericanExpress";
import { VisaCardIcon } from "../PaymentMethodIcons/Icons/Visa/Visa";
import { BancontactCardIcon } from "../PaymentMethodIcons/Icons/Bancontact/Bancontact";
import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { Text } from "@UI/Components/Text/Text";
import { Bold } from "@UI/Components/Bold/Bold";

export interface IPaymentMethodCardProps<T = boolean> {
  title: ReactNode;
  cardNetworkIcons: ICardNetwork[];
  onClick: (paymentMethod: string) => void;
  disabled?: T;
  "data-testid"?: string;
  value: string;
  forbidden?: boolean;
  disabledModalTitle: T extends true ? ReactNode : never;
  disabledModalContent: T extends true ? ReactNode : never;
}

type ICardNetworkIcons = {
  [key in ICardNetwork[number]]: FC;
};

export const CARD_NETWORK_ICONS: ICardNetworkIcons = {
  mastercard: MastercardCardIcon,
  americanExpress: AmericanExpressCardIcon,
  visa: VisaCardIcon,
  bancontact: BancontactCardIcon,
};

export const PaymentMethodCard: FC<IPaymentMethodCardProps> = ({
  title,
  onClick,
  cardNetworkIcons,
  disabled,
  forbidden,
  value,
  disabledModalTitle,
  disabledModalContent,
  ...rest
}) => {
  const [disabledModalOpen, setDisabledModalOpen] = useState(false);
  const { isDesktop } = useMediaQuery();

  disabled ||= forbidden;

  const handleClick = (e) => {
    e.preventDefault();
    if (disabled) {
      setDisabledModalOpen(!disabledModalOpen);
    } else {
      onClick(value);
    }
  };

  const handleCloseDisabledModal = () => {
    setDisabledModalOpen(false);
  };

  return (
    <>
      <Card
        variant={isDesktop ? "outline" : "solid"}
        marginBottom="3"
        background="white"
        cursor="pointer"
        data-disabled={disabled}
        opacity={disabled ? 0.5 : 1}
        h="20"
        py={[0, 0]}
        _hover={{
          borderColor: !disabled && "gray.500",
        }}
        onClick={handleClick}
        {...rest}
      >
        <Flex alignItems="center" justifyContent="space-between" h="full">
          <Flex alignItems="center">
            <Heading fontSize="md" marginRight="2" fontFamily="sans-serif">
              {title}
            </Heading>
            <HStack spacing="2">
              <>
                {cardNetworkIcons.map((cardNetwork) => {
                  const CardIcon = CARD_NETWORK_ICONS[cardNetwork];
                  if (!CardIcon) return null;

                  return <CardIcon key={cardNetwork} />;
                })}
              </>
              {forbidden && (
                <Icon as={CircleXmark} height="4" color="red.500" />
              )}
            </HStack>
          </Flex>
          <Icon as={ArrowRight} color="gray.500" marginLeft="3" />
        </Flex>
      </Card>
      <ResponsiveModal
        isOpen={disabledModalOpen}
        onClose={handleCloseDisabledModal}
      >
        <Box textAlign="center" data-testid="cash-unavailable-explanation">
          <Text
            size="lg"
            as={Bold}
            marginBottom="5"
            display="block"
            textAlign="center"
          >
            {disabledModalTitle}
          </Text>
          <Text textAlign="center">{disabledModalContent}</Text>
        </Box>
      </ResponsiveModal>
    </>
  );
};
